import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import Layout from "@/components/Layout";
import ConsentBanner from "@/components/ConsentBanner";
import TrackingListener from "@/components/TrackingListener";
import Home from "@/pages/Home";

// Route-basiertes Code-Splitting: Unterseiten, CRM und Rechtstexte laden erst bei Aufruf
const ServicePage = lazy(() => import("@/pages/ServicePage"));
const Angebot = lazy(() => import("@/pages/Angebot"));
const Kontakt = lazy(() => import("@/pages/Kontakt"));
const Danke = lazy(() => import("@/pages/Danke"));
const Referenzen = lazy(() => import("@/pages/Referenzen"));
const Legal = lazy(() => import("@/pages/Legal"));
const LegacyOrNotFound = lazy(() => import("@/pages/Legacy"));
const Admin = lazy(() => import("@/pages/Admin"));
const AdminLogin = lazy(() => import("@/pages/AdminLogin"));
const AuthCallback = lazy(() => import("@/pages/AuthCallback"));

const pageFallback = <div className="min-h-screen bg-[#0e0e11]" aria-hidden="true" />;

function AppRouter() {
  const location = useLocation();
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Emergent OAuth callback: session_id arrives in the URL fragment.
  if (location.hash?.includes("session_id=")) {
    return (
      <Suspense fallback={pageFallback}>
        <AuthCallback />
      </Suspense>
    );
  }
  return (
    <Suspense fallback={pageFallback}>
      <Routes>
        <Route element={<Layout />}>
          <Route path="/" element={<Home />} />
          <Route path="/cocktail-catering" element={<ServicePage serviceId="cocktail_catering" />} />
          <Route path="/barista-catering" element={<ServicePage serviceId="barista_catering" />} />
          <Route path="/messe-catering" element={<ServicePage serviceId="messe_catering" />} />
          <Route path="/hochzeitscatering" element={<ServicePage serviceId="hochzeitscatering" />} />
          <Route path="/personal" element={<ServicePage serviceId="personal" />} />
          <Route path="/cocktailkurse" element={<ServicePage serviceId="cocktailkurse" />} />
          <Route path="/referenzen" element={<Referenzen />} />
          <Route path="/angebot" element={<Angebot />} />
          <Route path="/kontakt" element={<Kontakt />} />
          <Route path="/danke" element={<Danke />} />
          <Route path="/impressum" element={<Legal kind="impressum" />} />
          <Route path="/datenschutz" element={<Legal kind="datenschutz" />} />
          <Route path="*" element={<LegacyOrNotFound />} />
        </Route>
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin" element={<Admin />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <div className="App">
      <HelmetProvider>
        <BrowserRouter>
          <AppRouter />
          <TrackingListener />
          <ConsentBanner />
          <Toaster position="top-center" theme="dark" />
        </BrowserRouter>
      </HelmetProvider>
    </div>
  );
}

export default App;
